import React, {
  useCallback, useContext, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonBar, ScreenHeader } from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

export function NewPOAKeepingAssetsSeparate() {
  const { powerOfAttorney, setPowerOfAttorney } = useContext(NewPOAContext);
  const navigate = useNavigate();
  const [ commingleFunds, setCommingleFunds ] = useState(powerOfAttorney.commingle_funds || false);
  
  useScrollToTop();

  const handleBack = useCallback(() => {
    navigate('../personal-benefit');
  }, [ navigate ]);

  const handleNext = useCallback(() => {
    setPowerOfAttorney(prev => ({ 
      ...prev, 
      commingle_funds: commingleFunds
    }));
    navigate('../paying-agent');
  }, [ commingleFunds, setPowerOfAttorney, navigate ]);
  
  return (
    <div className="new-poa-keeping-assets-separate screen-container screen">
      <ScreenHeader
        title="Keeping Your Assets Separate"
        description="Decide whether your agent may commingle funds with their own"
        handleBack={handleBack}
      />
      
      <div className="content-section">
        <h3>What Does Commingling Funds Mean?</h3>
        <p>
          Commingling funds means mixing your money or assets with your agent's 
          personal funds. Generally, your agent should keep your assets separate, but 
          there may be practical situations where this is difficult.
        </p>

        <h3>Examples of Commingling</h3>
        <ul className="commingling-examples">
          <li>Depositing your checks into the agent's personal account</li>
          <li>Using the agent's credit card for your expenses</li>
          <li>Mixing your cash with the agent's cash</li>
          <li>Joint ownership of accounts or property</li>
        </ul>

        <h3>Why Separation is Usually Better</h3>
        <ul className="separation-benefits">
          <li>Creates clear records of transactions</li>
          <li>Prevents confusion about ownership</li>
          <li>Protects your assets from the agent's creditors</li>
          <li>Reduces potential for disputes</li>
          <li>Makes accounting and reporting easier</li>
        </ul>

        <h3>Do You Want to Continue Commingling Funds?</h3>
        <div className="commingling-authorization">
          <div className="commingling-option">
            <label className="commingling-label">
              <input
                type="radio"
                name="commingle-funds"
                checked={!commingleFunds}
                onChange={() => setCommingleFunds(false)}
              />
              <div className="commingling-content">
                <h4>No, Keep Assets Separate</h4>
                <p>My agent must keep my assets separate from their own</p>
              </div>
            </label>
          </div>

          <div className="commingling-option">
            <label className="commingling-label">
              <input
                type="radio"
                name="commingle-funds"
                checked={commingleFunds}
                onChange={() => setCommingleFunds(true)}
              />
              <div className="commingling-content">
                <h4>Yes, Allow Commingling</h4>
                <p>My agent may commingle funds when practical</p>
              </div>
            </label>
          </div>
        </div>

        <div className="important-note">
          <h4>Important:</h4>
          <p>
            Even if you allow commingling, your agent must maintain careful records 
            of all transactions and be able to account for your funds separately. 
            Your agent remains responsible for protecting your assets.
          </p>
        </div>
      </div>

      <ButtonBar 
        handlePrimary={handleNext}
        primaryText="Continue"
      />
    </div>
  );
}
